<template>
  <TTabsToggle
    :header="$t('forms.settings_element_column')"
    no-move
    :changed="wasChanged"
    data-testid="column-settings"
  >
    <menu-content-container :padding-left-right="8">
      <content-row>
        <color-picker
          data-testid="column-background-color"
          :label="$t('forms.column_background_color')"
          position="left"
          :default-color="color.model"
          :changed="color.changed"
          :reset-tooltip="$t('forms.reset_value')"
          @color-change="color.model = $event"
          @reset="color.reset"
        ></color-picker>
        <t-input-number
          v-model="borderRadius.model"
          data-testid="column-border-radius"
          :label="$t('forms.button_border')"
          :min="0"
          :max="99"
          unit=""
          :changed="borderRadius.changed"
          :reset-tooltip="$t('forms.reset_value')"
          @reset="borderRadius.reset"
        ></t-input-number>
      </content-row>
      <content-row class="!block" data-testid="column-gradient-form">
        <TButton
          v-if="!gradient.model"
          data-testid="column-add-gradient"
          @click="addGradient"
        >
          <template #icon>
            <svg
              width="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19.958 3C20.3669 3 20.7597 3.16205 21.0488 3.45117C21.3379 3.74029 21.5 4.13312 21.5 4.54199V19.958C21.5 20.3669 21.3379 20.7597 21.0488 21.0488C20.7597 21.3379 20.3669 21.5 19.958 21.5H4.54199C4.13312 21.5 3.74029 21.3379 3.45117 21.0488C3.16205 20.7597 3 20.3669 3 19.958V4.54199C3 4.13312 3.16205 3.74029 3.45117 3.45117C3.74029 3.16205 4.13312 3 4.54199 3H19.958ZM10.708 4.54199V7.625H7.625V10.708H10.708V13.792H7.625V16.875H10.708V19.958H13.792V16.875H16.875V19.958H19.958V4.54199H16.875V7.625H13.792V4.54199H10.708ZM13.792 16.875H10.708V13.792H13.792V16.875ZM16.875 13.792H13.792V10.708H16.875V13.792ZM13.792 10.708H10.708V7.625H13.792V10.708Z"
                fill="currentColor"
              />
            </svg>
          </template>
          {{ $t("forms.add_gradient") }}
        </TButton>
        <TTabsToggle
          v-if="gradient.model"
          :header="$t('forms.gradient')"
          :changed="gradient.changed"
          default-open
          no-move
          show-remove
          can-be-reset
          @remove-block="gradient.model = undefined"
          @reset="gradient.reset"
        >
          <menu-content-container border-full :padding-left-right="8">
            <TGradient v-model="gradient.model" />
          </menu-content-container>
        </TTabsToggle>
      </content-row>

      <content-row :margin-bottom="16">
        <t-note>{{ $t("layout.background_gradient_text") }}</t-note>
      </content-row>
    </menu-content-container>
    <padding-form
      data-testid="column-padding-form"
      :label="$t('forms.column_margin')"
      :external-padding="columnPadding.model"
      :external-usage="true"
      :padding-left-right="8"
      :margin-bottom="8"
      :border-bottom="false"
      :changed="columnPadding.changed"
      :default-value="columnPadding.defaultValue"
      @paddingChanged="columnPadding.model = $event"
      @reset="columnPadding.reset"
    ></padding-form>

    <menu-content-container border-full :padding-left-right="8">
      <content-row>
        <TSwitch
          v-model="innerAttributes"
          data-testid="column-inner-attributes"
          :label="$t('forms.column_inner_margin')"
          :changed="innerAttrBinding.changed"
          :reset-tooltip="$t('forms.reset_value')"
          @reset="resetInnerAttributes"
        />
      </content-row>
    </menu-content-container>

    <border-form
      data-testid="column-border-form"
      color-picker-top
      :header-title="$t('forms.column_border')"
      :border="border.model"
      :default-border="border.defaultValue"
      :default-border-top="borderTop.defaultValue"
      :default-border-right="borderRight.defaultValue"
      :default-border-bottom="borderBottom.defaultValue"
      :default-border-left="borderLeft.defaultValue"
      :border-top="borderTop.model"
      :border-bottom="borderBottom.model"
      :border-left="borderLeft.model"
      :border-right="borderRight.model"
      :padding-left-right="8"
      :changed="borderChanged"
      :changed-top="borderTop.changed"
      :changed-right="borderRight.changed"
      :changed-bottom="borderBottom.changed"
      :changed-left="borderLeft.changed"
      @borderChanged="onBorderChanged"
      @reset="onBorderReset"
      @reset-top="resetBorderDirection('border-top')"
      @reset-right="resetBorderDirection('border-right')"
      @reset-bottom="resetBorderDirection('border-bottom')"
      @reset-left="resetBorderDirection('border-left')"
    >
    </border-form>
  </TTabsToggle>
</template>
<script lang="ts" setup>
  import colorPicker from "../../elements/colorPicker.vue";
  import paddingForm from "@/forms/paddingForm.vue";
  import BorderForm from "@/forms/borderForm.vue";
  import TGradient from "@/components/TGradient/TGradient.vue";
  import { useBlockAttr } from "@/composables/useBlockAttr";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import {
    TInputNumber,
    TNote,
    TSwitch,
    TButton,
    TTabsToggle,
  } from "@topol/twig-ui";
  import { computed } from "vue";

  const mjmlStore = useMjmlStore();
  const { nullable, px, anyChanged, resetAttr } = useBlockAttr({
    mode: "global",
    block: "mj-column",
  });

  // --- Composable bindings ---
  const color = nullable("background-color");
  const gradient = nullable("column-gradient");
  const borderRadius = px("border-radius", 0);
  const columnPadding = nullable("padding");
  const innerAttrBinding = nullable("inner-attributes");
  const border = nullable("border");
  const borderTop = nullable("border-top");
  const borderBottom = nullable("border-bottom");
  const borderLeft = nullable("border-left");
  const borderRight = nullable("border-right");

  function addGradient() {
    gradient.model = "linear-gradient(90deg, #ff7e5f 0%, #feb47b 100%)";
  }

  // --- Inner Attributes (CUSTOM - inverted boolean) ---

  const innerAttributes = computed({
    get() {
      return !innerAttrBinding.model;
    },
    set(value?: boolean) {
      if (value === undefined) {
        innerAttrBinding.model = undefined;
        return;
      }
      innerAttrBinding.model = !value;
    },
  });

  // --- Borders ---
  const borderChanged = anyChanged(
    border,
    borderTop,
    borderBottom,
    borderLeft,
    borderRight
  );
  function onBorderChanged({
    border: newBorder,
    borderType = "border",
  }: {
    border: string;
    borderType?: string;
  }) {
    if (borderType !== "border" && border.model !== undefined) {
      border.model = undefined;
    }

    const value =
      newBorder === undefined || newBorder.startsWith("0px")
        ? undefined
        : newBorder;

    border.model = value;
  }

  function onBorderReset() {
    const props = [
      "border",
      "border-top",
      "border-bottom",
      "border-left",
      "border-right",
    ] as const;
    for (const prop of props) {
      resetAttr(prop);
    }
  }

  function resetBorderDirection(prop: string) {
    resetAttr(prop);
  }

  function resetInnerAttributes() {
    innerAttributes.value =
      innerAttrBinding.defaultValue !== undefined
        ? !innerAttrBinding.defaultValue
        : undefined;
  }

  // --- Was Changed ---
  const wasChanged = computed(() => {
    return anyChanged(
      color,
      gradient,
      borderRadius,
      columnPadding,
      border,
      borderTop,
      borderBottom,
      borderLeft,
      borderRight,
      innerAttrBinding
    ).value;
  });
</script>
